/**
 * ConversationThread Component
 *
 * Displays messages in a conversation thread
 */

import React, { useEffect, useRef } from 'react';
import { Conversation, Message } from '../../../store/api/conversationsApi';
import { format } from 'date-fns';

interface ConversationThreadProps {
  conversation: Conversation;
  currentUserId: string;
  isLoading: boolean;
}

export const ConversationThread: React.FC<ConversationThreadProps> = ({
  conversation,
  currentUserId,
  isLoading,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages]);

  if (isLoading) {
    return (
      <div className="flex-1 p-6 space-y-4 overflow-y-auto">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}
          >
            <div className="max-w-xs p-3 rounded-lg bg-gray-100 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!conversation.messages || conversation.messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <svg
            className="w-16 h-16 text-gray-300 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="text-gray-500 font-medium mb-2">No messages yet</p>
          <p className="text-sm text-gray-400">
            Send a message to start the conversation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 sm:p-6 space-y-4 overflow-y-auto bg-gray-50 min-h-0">
      {conversation.messages.map((message: Message) => {
        const isCurrentUser = message.sender === currentUserId;

        return (
          <div
            key={message.id}
            className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs md:max-w-md lg:max-w-lg px-4 py-3 rounded-lg ${
                isCurrentUser
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-900 border border-gray-200'
              }`}
            >
              {/* Sender name (only for other users) */}
              {!isCurrentUser && message.sender_name && (
                <p className="text-xs font-semibold text-gray-500 mb-1">
                  {message.sender_name}
                </p>
              )}

              {/* Message content */}
              <p className="text-sm whitespace-pre-wrap break-words">
                {message.content}
              </p>

              {/* Timestamp */}
              <div className="flex items-center gap-2 mt-2">
                <p
                  className={`text-xs ${
                    isCurrentUser ? 'text-blue-100' : 'text-gray-400'
                  }`}
                >
                  {format(new Date(message.created_at), 'MMM d, h:mm a')}
                </p>

                {/* Email sent indicator */}
                {message.email_sent && (
                  <span
                    className={`text-xs ${
                      isCurrentUser ? 'text-blue-100' : 'text-gray-400'
                    }`}
                    title="Email notification sent"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};
